import { useState, forwardRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  useTheme,
  MenuItem,
  Alert,
  Snackbar,
  Slide,
  Fade,
  CircularProgress,
  Portal,
  Divider,
} from "@mui/material";
import { tokens } from "../../theme";
import { Formik } from "formik";
import * as yup from "yup";
import { createUser, updateUser } from "../../services/UserService";

// Transition for the Dialog (slides up)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transition for the Snackbar (slides in from the right)
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const UserForm = ({
  open,
  handleClose,
  onUserAdded,
  onUserEdited,
  editUser,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const editMode = Boolean(editUser);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [phase, setPhase] = useState(null);

  const initialValues = {
    fullName: editUser?.fullName || "",
    email: editUser?.email || "",
    location: editUser?.location || "",
    username: editUser?.username || "",
    role: editUser?.role || "",
  };

  const userSchema = yup.object().shape({
    fullName: yup.string().required("This field is required"),
    email: yup
      .string()
      .email("Invalid email")
      .required("This field is required"),
    location: yup.string().required("This field is required"),
    username: yup.string().required("This field is required"),
    role: yup.string().required("This field is required"),
  });

  const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
    setPhase("loading");
    return new Promise(async (resolve) => {
      try {
        const token = localStorage.getItem("token");
        let response;
        if (editMode) {
          response = await updateUser(editUser.id, values, token);
        } else {
          response = await createUser(values, token);
        }

        // Keep loading state visible for 1.5 seconds
        setTimeout(() => {
          setPhase(null);
          resetForm();
          handleClose();

          // Refresh the table
          if (editMode) {
            onUserEdited(response.data);
          } else {
            onUserAdded(response.data);
          }

          // Show the snackbar slightly after the table refreshes
          setTimeout(() => {
            setSnackbarMessage(
              `User ${editMode ? "updated" : "created"} successfully!`,
            );
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            resolve();
          }, 300);
        }, 1500);
      } catch (error) {
        console.error(
          `Failed to ${editMode ? "update" : "create"} user`,
          error,
        );
        setPhase(null);
        setSnackbarMessage(
          `Failed to ${editMode ? "update" : "create"} user. Please try again.`,
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        resolve();
      }
    });
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Shared input styling to match the modern aesthetic
  const inputStyleProps = {
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        borderColor: colors.blueAccent[500],
      },
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "12px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          sx={{ fontSize: "1.25rem", fontWeight: "700", pt: 3, pb: 2 }}
        >
          {editMode ? "Edit User Details" : "Create User"}
        </DialogTitle>

        <Divider
          sx={{
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.12)",
          }}
        />

        <Formik
          initialValues={initialValues}
          enableReinitialize={
            true
          } /* Fix: Allows Formik to update when editUser prop changes */
          onSubmit={handleFormSubmit}
          validationSchema={userSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            resetForm,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent sx={{ p: 3 }}>
                <Box
                  display="grid"
                  gap="16px"
                  mt="8px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Full Name"
                    name="fullName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.fullName}
                    error={!!touched.fullName && !!errors.fullName}
                    helperText={touched.fullName && errors.fullName}
                    sx={{ gridColumn: "span 4", ...inputStyleProps }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="email"
                    label="Email"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                    error={!!touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                      ...inputStyleProps,
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Location"
                    name="location"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.location}
                    error={!!touched.location && !!errors.location}
                    helperText={touched.location && errors.location}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                      ...inputStyleProps,
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Username"
                    name="username"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.username}
                    error={!!touched.username && !!errors.username}
                    helperText={touched.username && errors.username}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                      ...inputStyleProps,
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    select
                    fullWidth
                    variant="filled"
                    label="Role"
                    name="role"
                    value={values.role}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!touched.role && !!errors.role}
                    helperText={touched.role && errors.role}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                      ...inputStyleProps,
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                    SelectProps={{
                      MenuProps: {
                        TransitionComponent: Fade,
                        transitionDuration: 300,
                        PaperProps: {
                          sx: {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                                : "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(16px)",
                            border: `1px solid ${
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.15)"
                                : "rgba(0, 0, 0, 0.1)"
                            }`,
                            boxShadow:
                              theme.palette.mode === "dark"
                                ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                                : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
                            borderRadius: "12px",
                            mt: 1,
                            "& .MuiMenuItem-root": {
                              padding: "12px 20px",
                              margin: "4px 8px",
                              gap: "12px",
                              borderRadius: "8px",
                              transition: "all 0.2s ease-in-out",
                              color: colors.gray[100],
                              fontWeight: 600,
                              fontSize: "14px",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.1)"
                                    : "rgba(0, 0, 0, 0.04)",
                              },
                              "&:active": { transform: "scale(0.95)" },
                              "&.Mui-selected": {
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.15)"
                                    : "rgba(0, 0, 0, 0.08)",
                                color: colors.blueAccent[500],
                              },
                              "&.Mui-selected:hover": {
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.2)"
                                    : "rgba(0, 0, 0, 0.12)",
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="USER">USER</MenuItem>
                    <MenuItem value="ADMIN">ADMIN</MenuItem>
                  </TextField>
                </Box>
              </DialogContent>

              <Divider
                sx={{
                  mb: 0,
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.12)",
                }}
              />

              <DialogActions
                sx={{
                  px: 3,
                  pb: 3,
                  pt: { xs: 2, sm: 3 },
                  gap: { xs: 2, sm: 2 },
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  justifyContent: { xs: "center", sm: "flex-end" },
                  "& > :not(style) ~ :not(style)": {
                    ml: "0 !important",
                  },
                }}
              >
                <Button
                  onClick={() => {
                    resetForm();
                    handleClose();
                  }}
                  variant="outlined"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    color: colors.gray[100],
                    borderColor: colors.gray[500],
                    textTransform: "none",
                    fontWeight: "600",
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1.2, sm: 1.2 },
                    borderRadius: "8px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      borderColor: colors.gray[300],
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={phase === "loading"}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    backgroundColor: colors.blueAccent[600],
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: "600",
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1.2, sm: 1.2 },
                    borderRadius: "8px",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 4px 10px rgba(0,0,0,0.3)"
                        : "0 4px 12px rgba(79, 70, 229, 0.2)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: colors.blueAccent[500],
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 6px 14px rgba(0,0,0,0.4)"
                          : "0 6px 16px rgba(79, 70, 229, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(0,0,0,0.12)",
                      color:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(0,0,0,0.3)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {phase === "loading" ? (
                    <>
                      {editMode ? "Updating" : "Creating"}&nbsp;
                      <CircularProgress
                        size={18}
                        sx={{ color: "inherit", ml: "8px" }}
                      />
                    </>
                  ) : editMode ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: "100%",
              backgroundColor:
                snackbarSeverity === "success"
                  ? colors.greenAccent[700]
                  : colors.redAccent[700],
              color: "#ffffff",
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 4px 12px rgba(0, 0, 0, 0.4)"
                  : "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default UserForm;
